import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Crop,
  Info,
  Loader2,
  Plus,
  Sparkles,
  Square,
} from 'lucide-react';

import { TtqibEmbeddedImage } from '../features/ttqib/TtqibEmbeddedImage';
import { assetService } from '../services/assetService';
import {
  dajiaoService,
  type GeometryCandidate,
  type GeometryGeneratorId,
} from '../services/dajiaoService';
import { knowledgeService } from '../services/knowledgeService';
import type { KnowledgeParticle } from '../types/domain';
import {
  patchParticleEmbeddedImageMetadata,
  readParticleEmbeddedImage,
  readParticleEmbeddedImageActive,
  readParticleEmbeddedImageActiveModel,
  readParticleEmbeddedImageOriginal,
  readParticleGeometryCandidates,
  readParticleSourceAssetId,
  removeParticleEmbeddedImageMetadata,
  resolveParticleEmbedSource,
  selectParticleEmbeddedImageVariant,
} from '../utils/embeddedImageMetadata';
import {
  readParticleEmbedImageKind,
  resolveModelOrder,
  type EmbedImageKind,
  VENDOR_KIND_BY_ID,
} from '../utils/geometryVendorKinds';
import {
  clearSessionCandidates,
  consumeFinishedParticleUpdate,
  ensureGeometryRegenReconcile,
  getPendingGeometryModels,
  loadSessionCandidates,
  loadSessionSlideIndex,
  mergeCandidates,
  saveSessionCandidates,
  saveSessionSlideIndex,
  startGeometryRegen,
  subscribeGeometryRegen,
} from '../utils/geometryRegenJobStore';
import { ImageCropEditorModal } from './ImageCropEditorModal';
import { ProxyImage } from './assets/ProxyImage';

type PreferredEmbedSource = {
  sourceAssetId?: string;
  sourceImageUrl: string;
  ossId?: string;
};

type KnowledgeParticleEmbeddedImageSectionProps = {
  particle: KnowledgeParticle;
  onParticleUpdated: (particle: KnowledgeParticle) => void;
  /**
   * 页面上已展示的同源素材（如 AssetFittedGraphView 预览）。
   * 有则「添加嵌入图片」可立刻打开裁切，避免再打 workspace/asset 接口。
   */
  preferredSource?: PreferredEmbedSource | null;
};

type ModelOption = {
  id: GeometryGeneratorId;
  label: string;
  model: string;
  hint: string;
  comingSoon?: boolean;
  vendorKind: EmbedImageKind;
};

const MODEL_OPTION_CATALOG: Record<GeometryGeneratorId, ModelOption> = {
  'dajiao-qwen-agent': {
    id: 'dajiao-qwen-agent',
    label: 'DQA',
    model: 'qwen3.7-plus',
    hint: 'GT · 内部代号',
    vendorKind: 'GT',
  },
  dajiao: {
    id: 'dajiao',
    label: 'DJG',
    model: 'dinogeo-1-pro',
    hint: 'GT · 内部代号',
    vendorKind: 'GT',
  },
  'gpt-image-2': {
    id: 'gpt-image-2',
    label: 'OI2',
    model: 'gpt-image-2',
    hint: 'NGT · GPT Image 2（WaveSpeed edit）',
    vendorKind: 'NGT',
  },
  'qwen-image-3.0': {
    id: 'qwen-image-3.0',
    label: 'QI3',
    model: 'qwen-image-3.0',
    hint: '已下线',
    comingSoon: true,
    vendorKind: 'NGT',
  },
  'qwen-image-3.0-pro': {
    id: 'qwen-image-3.0-pro',
    label: 'QI3P',
    model: 'qwen-image-3.0-pro',
    hint: 'NGT · Qwen Image 3.0 Pro（DashScope）',
    vendorKind: 'NGT',
  },
};

function orderedModelOptions(kind: EmbedImageKind | null | undefined): ModelOption[] {
  return resolveModelOrder(kind).map((id) => MODEL_OPTION_CATALOG[id]);
}

/** 默认槽：进行中 > 已选用正式图 > 会话记忆浏览位 > 原始裁切 */
function resolveDefaultSlideIndex(input: {
  particleId: string;
  order: GeometryGeneratorId[];
  pending: GeometryGeneratorId[];
  activeVariant: string;
  activeModel: string;
}): number {
  const { particleId, order, pending, activeVariant, activeModel } = input;
  const slotCount = 1 + order.length;

  if (pending[0]) {
    const pendingIdx = 1 + order.indexOf(pending[0]);
    if (pendingIdx >= 1) return pendingIdx;
  }

  if (activeVariant === 'generated' && activeModel) {
    const byId = order.indexOf(activeModel as GeometryGeneratorId);
    if (byId >= 0) return 1 + byId;
    const byModel = order.findIndex(
      (id) => MODEL_OPTION_CATALOG[id]?.model === activeModel,
    );
    if (byModel >= 0) return 1 + byModel;
  }

  if (activeVariant === 'original') return 0;

  const remembered = loadSessionSlideIndex(particleId);
  if (remembered != null && remembered < slotCount) return remembered;

  return 0;
}

type SlotKey = 'original' | GeometryGeneratorId;

type CarouselSlot =
  | { key: 'original'; kind: 'original' }
  | {
      key: GeometryGeneratorId;
      kind: 'model';
      option: ModelOption;
      candidate: GeometryCandidate | null;
      pending: boolean;
    };

function formatCostNumber(costCny: number | null | undefined, estimated?: boolean): string {
  const n = Number(costCny);
  if (!Number.isFinite(n)) return '—';
  const text = n.toFixed(3);
  return estimated ? `约${text}` : text;
}

function EmbedConfirmDialog(props: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  confirming?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!props.open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center"
      onClick={props.onCancel}
    >
      <div
        className="w-full max-w-sm rounded-t-xl border border-border bg-surface p-4 shadow-xl sm:rounded-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="text-sm font-semibold text-ink">{props.title}</p>
        <p className="mt-1.5 text-xs leading-relaxed text-muted">{props.message}</p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="flex-1 rounded-lg border border-border py-2 text-sm text-muted hover:bg-black/[0.03] disabled:opacity-50"
            disabled={props.confirming}
            onClick={props.onCancel}
          >
            取消
          </button>
          <button
            type="button"
            className="flex-1 rounded-lg bg-danger py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            disabled={props.confirming}
            onClick={props.onConfirm}
          >
            {props.confirming ? '处理中…' : (props.confirmLabel || '确认清除')}
          </button>
        </div>
      </div>
    </div>
  );
}

function CostInfoBadge(props: {
  costCny: number | null | undefined;
  estimated?: boolean;
  visible: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="absolute right-1.5 top-1.5 z-10 flex items-center gap-1 rounded-md border border-border/80 bg-canvas/90 px-1 py-0.5 shadow-sm backdrop-blur">
      <button
        type="button"
        title={props.visible ? '隐藏费用' : '显示费用'}
        aria-label={props.visible ? '隐藏费用' : '显示费用'}
        aria-pressed={props.visible}
        className="inline-flex h-5 w-5 items-center justify-center rounded text-muted hover:bg-black/[0.05] hover:text-ink"
        onClick={props.onToggle}
      >
        <Info size={12} />
      </button>
      {props.visible ? (
        <span className="pr-0.5 text-[10px] font-medium tabular-nums text-ink/80">
          {formatCostNumber(props.costCny, props.estimated)}
        </span>
      ) : null}
    </div>
  );
}

export function KnowledgeParticleEmbeddedImageSection({
  particle,
  onParticleUpdated,
  preferredSource = null,
}: KnowledgeParticleEmbeddedImageSectionProps) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSource, setLoadingSource] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceAssetId, setSourceAssetId] = useState('');
  const [originalOssId, setOriginalOssId] = useState<string | undefined>();
  const [pendingModels, setPendingModels] = useState<GeometryGeneratorId[]>([]);
  const [adopting, setAdopting] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<GeometryCandidate[]>([]);
  const [slideIndex, setSlideIndex] = useState(0);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [costVisibleBySlot, setCostVisibleBySlot] = useState<Partial<Record<SlotKey, boolean>>>({});
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const modelMenuRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(true);
  const activeParticleIdRef = useRef(particle.id);
  const onParticleUpdatedRef = useRef(onParticleUpdated);
  onParticleUpdatedRef.current = onParticleUpdated;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const metadata = (particle.metadata ?? null) as Record<string, unknown> | null;

  const embeddedImage = useMemo(
    () => readParticleEmbeddedImage(metadata),
    [metadata],
  );

  const originalEmbedded = useMemo(
    () => readParticleEmbeddedImageOriginal(metadata) || embeddedImage,
    [metadata, embeddedImage],
  );

  const activeVariant = useMemo(
    () => readParticleEmbeddedImageActive(metadata),
    [metadata],
  );

  const activeModel = useMemo(
    () => readParticleEmbeddedImageActiveModel(metadata),
    [metadata],
  );

  const particleSourceAssetId = useMemo(
    () => readParticleSourceAssetId(metadata),
    [metadata],
  );

  /** 分类器已落库的 kind；未返回前为 null，此时生图走马灯序列未确定 */
  const classifiedKind = useMemo(
    () => readParticleEmbedImageKind(metadata),
    [metadata],
  );

  const embedKind = classifiedKind || 'GT';

  const modelOptions = useMemo(
    () => orderedModelOptions(embedKind),
    [embedKind],
  );

  const modelOrder = useMemo(
    () => resolveModelOrder(embedKind),
    [embedKind],
  );

  /** 分类完成且不在重跑中，才允许浏览/生成重生图槽 */
  const carouselReady = Boolean(classifiedKind) && !classifying;

  useEffect(() => {
    activeParticleIdRef.current = particle.id;
    const fromMeta = readParticleGeometryCandidates(metadata) as GeometryCandidate[];
    const fromSession = loadSessionCandidates(particle.id);
    const kind = readParticleEmbedImageKind(metadata);
    const order = resolveModelOrder(kind || 'GT');
    const merged = mergeCandidates(fromSession, fromMeta, order);
    setCandidates(merged);
    // SPA 内离开再回来：从模块级 + session 恢复「生成中」
    const pending = getPendingGeometryModels(particle.id);
    setPendingModels(pending);
    setCostVisibleBySlot({});
    setClearConfirmOpen(false);
    // 收起/离页期间已完成的落库结果，展开时补通知父级
    const finished = consumeFinishedParticleUpdate(particle.id);
    if (finished) onParticleUpdatedRef.current(finished);
    // 有 pending 无 inflight（刷新等）时轮询落库；有 inflight 则原请求继续
    ensureGeometryRegenReconcile(particle.id);
  }, [particle.id]);

  // 分类就绪后定位默认槽；未分类则锁原始裁切并隐藏两侧箭头
  useEffect(() => {
    if (!carouselReady) {
      setSlideIndex(0);
      return;
    }
    const order = resolveModelOrder(classifiedKind);
    setSlideIndex(resolveDefaultSlideIndex({
      particleId: particle.id,
      order,
      pending: getPendingGeometryModels(particle.id),
      activeVariant,
      activeModel,
    }));
  }, [particle.id, carouselReady, classifiedKind, activeVariant, activeModel]);

  // 浏览位写入会话，收起再展开可恢复（正式选用优先于记忆）
  useEffect(() => {
    if (!carouselReady) return;
    saveSessionSlideIndex(particle.id, slideIndex);
  }, [slideIndex, carouselReady, particle.id]);

  useEffect(() => {
    const persisted = readParticleGeometryCandidates(metadata) as GeometryCandidate[];
    const order = resolveModelOrder(readParticleEmbedImageKind(metadata) || 'GT');
    setCandidates((prev) => {
      const merged = mergeCandidates(prev, persisted, order);
      saveSessionCandidates(particle.id, merged);
      return merged;
    });
  }, [metadata, particle.id]);

  // 跨卸载订阅：后台完成后写回当前颗粒 UI
  useEffect(() => {
    return subscribeGeometryRegen((event) => {
      if (event.particleId !== activeParticleIdRef.current) return;
      if (event.type === 'started') {
        setPendingModels(getPendingGeometryModels(event.particleId));
        return;
      }
      setPendingModels(getPendingGeometryModels(event.particleId));
      setCandidates(event.candidates);
      if (event.particle) {
        consumeFinishedParticleUpdate(event.particleId);
        onParticleUpdatedRef.current(event.particle);
      }
      if (event.error) {
        setRegenError(event.error);
      }
    });
  }, []);

  const slots = useMemo<CarouselSlot[]>(() => {
    // 分类未定序前只展示原始裁切，避免用默认 GT 顺序假装生图序列已就绪
    if (!carouselReady) {
      return [{ key: 'original', kind: 'original' }];
    }
    const pendingSet = new Set(pendingModels);
    return [
      { key: 'original', kind: 'original' },
      ...modelOptions.map((option) => ({
        key: option.id,
        kind: 'model' as const,
        option,
        candidate: candidates.find((item) => item.id === option.id) || null,
        pending: pendingSet.has(option.id),
      })),
    ];
  }, [candidates, pendingModels, modelOptions, carouselReady]);

  const safeSlideIndex = slots.length > 0
    ? ((slideIndex % slots.length) + slots.length) % slots.length
    : 0;
  const currentSlot = slots[safeSlideIndex] || slots[0];
  const showCarouselNav = carouselReady && slots.length > 1;
  const regenLoading = pendingModels.length > 0;

  useEffect(() => {
    if (slots.length === 0) return;
    if (slideIndex < 0 || slideIndex >= slots.length) {
      setSlideIndex(safeSlideIndex);
    }
  }, [slideIndex, slots.length, safeSlideIndex]);

  useEffect(() => {
    if (!modelMenuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!modelMenuRef.current?.contains(event.target as Node)) {
        setModelMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [modelMenuOpen]);

  useEffect(() => {
    let cancelled = false;
    if (!originalEmbedded) {
      setOriginalOssId(undefined);
      return;
    }
    void (async () => {
      const originalMeta = asOriginalSource(metadata);
      // 原始槽只用原裁切的 sourceAssetId，避免正式图切到生成图后误用生成素材 ossId
      const assetId = originalMeta?.sourceAssetId
        || (activeVariant === 'original' ? particleSourceAssetId : '');
      const fromUrl = extractOssKeyFromUrl(
        originalMeta?.sourceImageUrl || originalEmbedded.sourceImageUrl,
      );

      if (assetId) {
        try {
          const asset = await assetService.getAsset(assetId);
          const next = String(asset.oss_id || '').trim()
            || extractOssKeyFromUrl(asset.url)
            || fromUrl;
          if (!cancelled) setOriginalOssId(next || undefined);
          return;
        } catch {
          // fall through to URL key
        }
      }

      if (!cancelled) setOriginalOssId(fromUrl || undefined);
    })();
    return () => {
      cancelled = true;
    };
  }, [originalEmbedded, metadata, particleSourceAssetId, activeVariant]);

  // 预热：父级已有预览图时先写入本地，点击时无需再等网络
  useEffect(() => {
    const url = String(preferredSource?.sourceImageUrl || '').trim();
    if (!url) return;
    setSourceUrl((prev) => prev || url);
    const assetId = String(preferredSource?.sourceAssetId || '').trim();
    if (assetId) setSourceAssetId((prev) => prev || assetId);
    const oss = String(preferredSource?.ossId || '').trim()
      || extractOssKeyFromUrl(url);
    if (oss) setOriginalOssId((prev) => prev || oss);
  }, [preferredSource?.sourceImageUrl, preferredSource?.sourceAssetId, preferredSource?.ossId]);

  const openEditor = async () => {
    setError(null);

    // 编辑选区始终针对原始素材裁切槽
    const originalMeta = asOriginalSource(metadata);
    const fastUrl = String(
      originalMeta?.sourceImageUrl
      || sourceUrl
      || preferredSource?.sourceImageUrl
      || '',
    ).trim();
    const fastAssetId = String(
      originalMeta?.sourceAssetId
      || sourceAssetId
      || preferredSource?.sourceAssetId
      || '',
    ).trim();
    const fastOss = String(
      originalOssId
      || preferredSource?.ossId
      || extractOssKeyFromUrl(fastUrl)
      || '',
    ).trim();

    // 快路径：已有 URL（与上方预览同源）→ 立刻开弹窗，不阻塞签名/拉资产
    if (fastUrl) {
      setSourceUrl(fastUrl);
      if (fastAssetId) setSourceAssetId(fastAssetId);
      if (fastOss) setOriginalOssId(fastOss);
      setEditorOpen(true);
      // 后台补全 ossId（若缺），不挡 UI
      if (fastAssetId && !fastOss) {
        void assetService.getAsset(fastAssetId).then((asset) => {
          const next = String(asset.oss_id || '').trim();
          if (next) setOriginalOssId(next);
        }).catch(() => undefined);
      }
      return;
    }

    setLoadingSource(true);
    try {
      const source = await resolveParticleEmbedSource(particle);
      if (!source?.sourceImageUrl) {
        setError('未找到关联素材，暂时无法添加嵌入图');
        return;
      }
      setSourceUrl(source.sourceImageUrl);
      setSourceAssetId(source.sourceAssetId);
      setOriginalOssId(source.ossId || originalOssId);
      setEditorOpen(true);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载素材失败');
    } finally {
      setLoadingSource(false);
    }
  };

  const handleGenerateWithModel = (modelId: GeometryGeneratorId) => {
    const option = MODEL_OPTION_CATALOG[modelId];
    if (option?.comingSoon) {
      setRegenError('coming soon，内测中');
      setModelMenuOpen(false);
      return;
    }

    setModelMenuOpen(false);
    setRegenError(null);

    // 仅在发起时跳到对应槽；完成后不抢焦点，切走马灯不中断请求
    const targetIdx = 1 + modelOptions.findIndex((item) => item.id === modelId);
    if (targetIdx >= 1) setSlideIndex(targetIdx);

    const started = startGeometryRegen({
      particleId: particle.id,
      modelId,
      ossId: originalOssId,
      modelOrder,
      modelLabel: option?.label,
      seedCandidates: candidates,
    });
    if (started) {
      setPendingModels(getPendingGeometryModels(particle.id));
    }
  };

  const adoptOriginalLocally = async () => {
    const nextMetadata = {
      ...selectParticleEmbeddedImageVariant(particle.metadata ?? null, 'original'),
      relatedGraphEmbeddedImageActiveModel: null,
    };
    const updated = await knowledgeService.updateKnowledgeParticle(particle.id, {
      metadata: nextMetadata,
    });
    onParticleUpdated(updated);
  };

  const adoptGeneratedLocally = async (input: {
    candidateId: GeometryGeneratorId;
    pngUrl?: string | null;
    assetId?: string | null;
    width?: number;
    height?: number;
    model?: string;
  }) => {
    const sourceImageUrl = String(input.pngUrl || '').trim();
    if (!sourceImageUrl && !input.assetId) {
      throw new Error('当前槽位尚无可用生成图');
    }
    let resolvedUrl = sourceImageUrl;
    if (!resolvedUrl && input.assetId) {
      const asset = await assetService.getAsset(input.assetId);
      resolvedUrl = String(asset.url || '').trim();
    }
    if (!resolvedUrl) throw new Error('无法解析生成图地址');
    const nextMetadata = {
      ...selectParticleEmbeddedImageVariant(particle.metadata ?? null, 'generated', {
        sourceAssetId: input.assetId || undefined,
        sourceImageUrl: resolvedUrl,
        imageWidth: input.width || 1024,
        imageHeight: input.height || 1024,
      }),
      relatedGraphEmbeddedImageActiveModel: input.candidateId,
    };
    const updated = await knowledgeService.updateKnowledgeParticle(particle.id, {
      metadata: nextMetadata,
    });
    onParticleUpdated(updated);
  };

  const handleAdoptCurrent = async () => {
    if (!currentSlot) return;

    if (currentSlot.kind === 'original') {
      setAdopting(true);
      setRegenError(null);
      try {
        const result = await dajiaoService.adoptOriginal({ particleId: particle.id });
        onParticleUpdated(result.particle);
      } catch (adoptError) {
        const status = (adoptError as { statusCode?: number } | null)?.statusCode;
        if (status === 404) {
          try {
            await adoptOriginalLocally();
            return;
          } catch (fallbackError) {
            setRegenError(
              fallbackError instanceof Error ? fallbackError.message : '恢复原始嵌入图失败',
            );
            return;
          }
        }
        setRegenError(adoptError instanceof Error ? adoptError.message : '恢复原始嵌入图失败');
      } finally {
        setAdopting(false);
      }
      return;
    }

    const selectedCandidate = currentSlot.candidate;
    if (!selectedCandidate?.pngUrl && !selectedCandidate?.ossId && !selectedCandidate?.assetId) {
      setRegenError('当前槽位尚无可用生成图');
      return;
    }

    setAdopting(true);
    setRegenError(null);
    try {
      const result = await dajiaoService.adoptGenerated({
        particleId: particle.id,
        pngUrl: selectedCandidate.pngUrl || undefined,
        ossId: selectedCandidate.ossId || undefined,
        assetId: selectedCandidate.assetId || undefined,
        width: selectedCandidate.width,
        height: selectedCandidate.height,
        model: selectedCandidate.model,
        candidateId: currentSlot.key,
        artifactUrl: selectedCandidate.artifactUrl || undefined,
      });
      onParticleUpdated(result.particle);
    } catch (adoptError) {
      const status = (adoptError as { statusCode?: number } | null)?.statusCode;
      if (status === 404) {
        try {
          await adoptGeneratedLocally({
            candidateId: currentSlot.key,
            pngUrl: selectedCandidate.pngUrl,
            assetId: selectedCandidate.assetId,
            width: selectedCandidate.width,
            height: selectedCandidate.height,
            model: selectedCandidate.model,
          });
          return;
        } catch (fallbackError) {
          setRegenError(
            fallbackError instanceof Error
              ? fallbackError.message
              : '采用生成图失败（服务端选用接口未部署，本地回退也失败）',
          );
          return;
        }
      }
      setRegenError(adoptError instanceof Error ? adoptError.message : '采用生成图失败');
    } finally {
      setAdopting(false);
    }
  };

  const runEmbedClassifier = async (particleId: string, ossId?: string) => {
    setClassifying(true);
    try {
      const data = await dajiaoService.classifyEmbedImage({
        particleId,
        ossId,
      });
      if (!mountedRef.current || activeParticleIdRef.current !== particleId) return;
      if (data.particle) onParticleUpdated(data.particle);
    } catch (classifyError) {
      if (mountedRef.current && activeParticleIdRef.current === particleId) {
        console.warn(
          '[embed] classify failed',
          classifyError instanceof Error ? classifyError.message : classifyError,
        );
      }
    } finally {
      if (mountedRef.current && activeParticleIdRef.current === particleId) {
        setClassifying(false);
      }
    }
  };

  const handleApplyCrop = async (result: {
    cropRect: { x: number; y: number; width: number; height: number };
    imageWidth: number;
    imageHeight: number;
  }) => {
    setSaving(true);
    setError(null);
    try {
      const nextMetadata = patchParticleEmbeddedImageMetadata(particle.metadata ?? null, {
        sourceAssetId: sourceAssetId || undefined,
        sourceImageUrl: sourceUrl,
        cropRect: result.cropRect,
        imageWidth: result.imageWidth,
        imageHeight: result.imageHeight,
      });
      const withActive = {
        ...nextMetadata,
        relatedGraphEmbeddedImageOriginal: nextMetadata.relatedGraphEmbeddedImage,
        relatedGraphEmbeddedImageActive: 'original',
        relatedGraphEmbeddedImageActiveModel: null,
        // 保留已持久化的走马灯候选；分类结果稍后由 classifier 覆盖
        relatedGraphGeometryCandidates:
          (particle.metadata as Record<string, unknown> | null)?.relatedGraphGeometryCandidates
          ?? nextMetadata.relatedGraphGeometryCandidates,
      };
      const updated = await knowledgeService.updateKnowledgeParticle(particle.id, {
        metadata: withActive,
      });
      onParticleUpdated(updated);
      setEditorOpen(false);
      setSlideIndex(0);
      // 后台分类：结合嵌入图 + 题干，决定走马灯 GT/NGT 排序
      void runEmbedClassifier(particle.id, originalOssId);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '保存嵌入图失败');
    } finally {
      setSaving(false);
    }
  };

  const handleClearEmbedConfirmed = async () => {
    setSaving(true);
    setError(null);
    try {
      const nextMetadata = removeParticleEmbeddedImageMetadata(particle.metadata ?? null);
      const updated = await knowledgeService.updateKnowledgeParticle(particle.id, {
        metadata: nextMetadata,
      });
      clearSessionCandidates(particle.id);
      setCandidates([]);
      onParticleUpdated(updated);
      setClearConfirmOpen(false);
      setEditorOpen(false);
      setSlideIndex(0);
    } catch (clearError) {
      setError(clearError instanceof Error ? clearError.message : '清除嵌入图失败');
    } finally {
      setSaving(false);
    }
  };

  const isOfficialOriginal = currentSlot?.kind === 'original' && activeVariant !== 'generated';
  const isOfficialModel =
    currentSlot?.kind === 'model'
    && activeVariant === 'generated'
    && Boolean(activeModel)
    && (activeModel === currentSlot.key || activeModel === currentSlot.candidate?.model);

  const canAdopt =
    currentSlot?.kind === 'original'
    || (currentSlot?.kind === 'model'
      && Boolean(
        currentSlot.candidate?.pngUrl
        || currentSlot.candidate?.ossId
        || currentSlot.candidate?.assetId,
      ));

  const costVisible = Boolean(currentSlot && costVisibleBySlot[currentSlot.key]);
  const toggleCost = () => {
    if (!currentSlot) return;
    setCostVisibleBySlot((prev) => ({
      ...prev,
      [currentSlot.key]: !prev[currentSlot.key],
    }));
  };

  if (embeddedImage || originalEmbedded) {
    const displayOriginal = originalEmbedded || embeddedImage;
    if (!displayOriginal) return null;

    return (
      <>
        <section className="rounded-xl border border-border bg-surface p-4 shadow-card">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">嵌入图</p>
              <span className="rounded-full bg-canvas px-2 py-0.5 text-[11px] text-subtle">
                正式：{activeVariant === 'generated'
                  ? (MODEL_OPTION_CATALOG[activeModel as GeometryGeneratorId]?.label
                    || modelOptions.find((item) => item.id === activeModel || item.model === activeModel)?.label
                    || '生成图')
                  : '原始裁切'}
              </span>
              {classifying ? (
                <span
                  className="rounded-full bg-canvas px-2 py-0.5 text-[11px] text-subtle"
                  title="正在判定几何/非几何，完成前不可浏览生图序列"
                >
                  分类中…
                </span>
              ) : !classifiedKind ? (
                <button
                  type="button"
                  className="rounded-full bg-canvas px-2 py-0.5 text-[11px] text-subtle hover:bg-black/[0.04] hover:text-ink"
                  title="点击重新分类"
                  onClick={() => { void runEmbedClassifier(particle.id, originalOssId); }}
                >
                  待分类
                </button>
              ) : (
                <span
                  className="rounded-full bg-canvas px-2 py-0.5 text-[11px] text-subtle"
                  title={
                    classifiedKind === 'GT'
                      ? '几何题图（GT）：当前仅开放 DQA'
                      : '非几何（NGT）：OI2 / QI3（均内测中），不展示 GT 槽'
                  }
                >
                  {classifiedKind === 'GT' ? 'GT · 几何' : 'NGT · 非几何'}
                </span>
              )}
            </div>
            <div className="relative" ref={modelMenuRef}>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted hover:bg-black/[0.03] disabled:opacity-50"
                disabled={loadingSource || saving || adopting || !carouselReady}
                onClick={() => setModelMenuOpen((open) => !open)}
                title={carouselReady ? '选择模型生成几何图' : '请等待嵌入图分类完成'}
                aria-haspopup="menu"
                aria-expanded={modelMenuOpen}
              >
                {regenLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {regenLoading ? '生成中…' : '生成图片'}
                <ChevronDown size={12} className={`transition ${modelMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {modelMenuOpen ? (
                <div
                  role="menu"
                  className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-lg border border-border bg-surface shadow-card"
                >
                  <p className="border-b border-border px-3 py-1.5 text-[10px] text-subtle">
                    选择生图模型（结果会持久化到走马灯槽位）
                  </p>
                  {modelOptions.map((option) => {
                    const existing = candidates.find((item) => item.id === option.id);
                    const pending = pendingModels.includes(option.id);
                    const comingSoon = Boolean(option.comingSoon);
                    const vendor = VENDOR_KIND_BY_ID[option.id];
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="menuitem"
                        disabled={pending || comingSoon}
                        className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-black/[0.03] disabled:opacity-50"
                        onClick={() => { void handleGenerateWithModel(option.id); }}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-medium text-ink">
                            {option.label}
                            <span className="ml-1 text-[10px] font-normal text-subtle">{vendor}</span>
                            {comingSoon ? (
                              <span className="ml-1 text-[10px] font-normal text-subtle">· 内测中</span>
                            ) : existing?.pngUrl ? (
                              <span className="ml-1 text-[10px] font-normal text-subtle">· 重跑</span>
                            ) : null}
                            {pending ? (
                              <span className="ml-1 text-[10px] font-normal text-primary">· 生成中</span>
                            ) : null}
                          </span>
                          <span className="block text-[10px] text-subtle">{option.hint}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden rounded-lg border border-border bg-white">
              {currentSlot?.kind === 'original' ? (
                <>
                  <CostInfoBadge
                    costCny={0}
                    estimated={false}
                    visible={costVisible}
                    onToggle={toggleCost}
                  />
                  <TtqibEmbeddedImage
                    record={displayOriginal}
                    ossId={originalOssId}
                    height={220}
                    width={640}
                    zoomable
                    zoomTitle="原始裁切"
                  />
                </>
              ) : null}

              {currentSlot?.kind === 'model' ? (
                <>
                  {currentSlot.candidate || currentSlot.pending ? (
                    <CostInfoBadge
                      costCny={currentSlot.candidate?.costCny ?? null}
                      estimated={currentSlot.candidate?.costEstimated}
                      visible={costVisible}
                      onToggle={toggleCost}
                    />
                  ) : null}

                  {currentSlot.pending ? (
                    <div className="flex h-[220px] flex-col items-center justify-center gap-2 bg-[#f7f9fc]">
                      <Loader2 className="animate-spin text-primary" size={22} />
                      <p className="text-xs text-muted">正在生成 {currentSlot.option.label}…</p>
                    </div>
                  ) : (currentSlot.candidate?.pngUrl || currentSlot.candidate?.ossId) ? (
                    <div className="flex h-[220px] items-center justify-center bg-[#f7f9fc]">
                      <ProxyImage
                        rawUrl={currentSlot.candidate.pngUrl}
                        ossId={currentSlot.candidate.ossId || undefined}
                        alt={currentSlot.option.label}
                        className="mx-auto max-h-[220px] w-auto max-w-full bg-transparent"
                        imgClassName="max-h-[220px] w-auto object-contain"
                        fallbackText="生成图加载失败"
                        zoomable
                        zoomTitle={currentSlot.option.label}
                      />
                    </div>
                  ) : (
                    <div className="flex h-[220px] flex-col items-center justify-center gap-2 bg-[#f7f9fc] px-4">
                      <p className="text-sm font-semibold tracking-wide text-ink">{currentSlot.option.label}</p>
                      <p className="text-center text-xs text-muted">
                        {currentSlot.option.comingSoon
                          ? 'coming soon，内测中'
                          : (currentSlot.candidate?.error || `尚未生成 ${currentSlot.option.label}`)}
                      </p>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2.5 py-1 text-xs text-ink hover:bg-black/[0.03] disabled:opacity-50"
                        disabled={currentSlot.pending || adopting || Boolean(currentSlot.option.comingSoon)}
                        onClick={() => { void handleGenerateWithModel(currentSlot.key); }}
                      >
                        <Sparkles size={12} />
                        {currentSlot.option.comingSoon ? 'coming soon，内测中' : '生成图片'}
                      </button>
                    </div>
                  )}
                </>
              ) : null}

              {showCarouselNav ? (
                <>
                  <button
                    type="button"
                    aria-label="上一张"
                    onClick={() => {
                      setSlideIndex((idx) => {
                        const len = Math.max(1, slots.length);
                        return (idx - 1 + len) % len;
                      });
                    }}
                    className="absolute left-1.5 top-1/2 z-10 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-surface/95 text-ink shadow-sm"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="下一张"
                    onClick={() => {
                      setSlideIndex((idx) => {
                        const len = Math.max(1, slots.length);
                        return (idx + 1) % len;
                      });
                    }}
                    className="absolute right-1.5 top-1/2 z-10 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-surface/95 text-ink shadow-sm"
                  >
                    <ChevronRight size={16} />
                  </button>
                </>
              ) : null}
            </div>

            <div className="mt-2 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
              <div className="flex min-w-0 items-center gap-1.5">
                <p className="truncate text-xs text-ink">
                  <span className="font-medium">
                    {currentSlot?.kind === 'original' ? '原始裁切' : currentSlot?.option.label}
                  </span>
                  <span className="text-subtle">
                    {currentSlot?.kind === 'model' && currentSlot.pending
                      ? ' · 生成中…'
                      : currentSlot?.kind === 'model' && currentSlot.candidate?.elapsedMs
                        ? ` · ${Math.max(1, Math.round(currentSlot.candidate.elapsedMs / 1000))}s`
                        : ''}
                  </span>
                </p>
              </div>

              <div className="flex items-center justify-center gap-1">
                {slots.map((slot, idx) => {
                  const slotPending = slot.kind === 'model' && slot.pending;
                  return (
                    <button
                      key={slot.key}
                      type="button"
                      aria-label={slotPending ? `第 ${idx + 1} 槽（生成中）` : `第 ${idx + 1} 槽`}
                      title={slotPending ? '后台生成中' : undefined}
                      onClick={() => setSlideIndex(idx)}
                      className={`h-1.5 rounded-full transition ${
                        idx === safeSlideIndex
                          ? 'w-4 bg-primary'
                          : slotPending
                            ? 'w-2.5 animate-pulse bg-primary/55'
                            : 'w-1.5 bg-border hover:bg-muted'
                      }`}
                    />
                  );
                })}
              </div>

              <div className="flex items-center justify-end gap-1">
                {currentSlot?.kind === 'original' ? (
                  <button
                    type="button"
                    title="编辑选区"
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted hover:bg-black/[0.03] disabled:opacity-50"
                    disabled={loadingSource || saving || adopting}
                    onClick={() => { void openEditor(); }}
                  >
                    {loadingSource ? <Loader2 size={12} className="animate-spin" /> : <Crop size={12} />}
                    裁切
                  </button>
                ) : (
                  <button
                    type="button"
                    title={
                      currentSlot?.option.comingSoon
                        ? 'coming soon，内测中'
                        : currentSlot?.candidate?.pngUrl
                          ? '重新生成'
                          : '生成图片'
                    }
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted hover:bg-black/[0.03] disabled:opacity-50"
                    disabled={currentSlot?.pending || adopting || Boolean(currentSlot?.option.comingSoon)}
                    onClick={() => {
                      if (currentSlot?.kind === 'model') {
                        void handleGenerateWithModel(currentSlot.key);
                      }
                    }}
                  >
                    {currentSlot?.pending
                      ? <Loader2 size={12} className="animate-spin" />
                      : <Sparkles size={12} />}
                    {currentSlot?.option.comingSoon
                      ? '内测中'
                      : currentSlot?.candidate?.pngUrl
                        ? '重新生成'
                        : '生成'}
                  </button>
                )}

                {canAdopt ? (
                  isOfficialOriginal || isOfficialModel ? (
                    <span
                      title="当前正式图"
                      className="inline-flex items-center gap-0.5 rounded-md border border-success/35 bg-[#F1FAF1] px-1.5 py-1 text-[11px] font-medium text-success"
                    >
                      <Check size={12} />
                      正式
                    </span>
                  ) : (
                    <button
                      type="button"
                      title="设为正式嵌入图"
                      className="inline-flex items-center gap-0.5 rounded-md border border-border px-2 py-1 text-[11px] text-muted hover:bg-black/[0.03] hover:text-ink disabled:opacity-50"
                      disabled={adopting || (currentSlot?.kind === 'model' && currentSlot.pending)}
                      onClick={() => { void handleAdoptCurrent(); }}
                    >
                      {adopting ? <Loader2 size={12} className="animate-spin" /> : <Square size={12} />}
                      选用
                    </button>
                  )
                ) : null}
              </div>
            </div>

          </div>

          {regenError ? (
            <div className="mt-3 rounded-lg border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
              {regenError}
            </div>
          ) : null}
        </section>

        {error ? <p className="text-sm text-danger">{error}</p> : null}

        <ImageCropEditorModal
          open={editorOpen}
          imageUrl={sourceUrl}
          ossId={originalOssId}
          initialCropRect={displayOriginal.cropRect}
          applying={saving}
          allowClearEmbed
          onClose={() => !saving && setEditorOpen(false)}
          onApply={handleApplyCrop}
          onClearEmbed={() => setClearConfirmOpen(true)}
        />

        <EmbedConfirmDialog
          open={clearConfirmOpen}
          title="清除嵌入图片？"
          message="清除嵌入图片后，走马灯中的全部生图候选也会一并清除，且不可从本页恢复。确认继续吗？"
          confirmLabel="确认清除"
          confirming={saving}
          onCancel={() => !saving && setClearConfirmOpen(false)}
          onConfirm={() => { void handleClearEmbedConfirmed(); }}
        />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-primary/30 bg-primary/5 px-4 py-3 text-sm text-primary hover:bg-primary/10 disabled:opacity-50"
        disabled={loadingSource || saving}
        onClick={() => { void openEditor(); }}
      >
        {loadingSource ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
        {loadingSource ? '加载素材中…' : '添加嵌入图片'}
      </button>

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <ImageCropEditorModal
        open={editorOpen}
        imageUrl={sourceUrl}
        ossId={originalOssId}
        applying={saving}
        onClose={() => !saving && setEditorOpen(false)}
        onApply={handleApplyCrop}
      />
    </>
  );
}

function asOriginalSource(metadata: Record<string, unknown> | null | undefined): {
  sourceAssetId?: string;
  sourceImageUrl?: string;
} | null {
  if (!metadata) return null;
  const original = metadata.relatedGraphEmbeddedImageOriginal;
  const active = String(metadata.relatedGraphEmbeddedImageActive || '').trim();
  const current = metadata.relatedGraphEmbeddedImage;
  // 生成图为正式时，current 已是生成素材，原始槽必须读 Original 备份
  const record = (original && typeof original === 'object' && !Array.isArray(original)
    ? original
    : active !== 'generated' && current && typeof current === 'object' && !Array.isArray(current)
      ? current
      : null) as Record<string, unknown> | null;
  if (!record) return null;
  return {
    sourceAssetId: typeof record.sourceAssetId === 'string' ? record.sourceAssetId : undefined,
    sourceImageUrl: typeof record.sourceImageUrl === 'string' ? record.sourceImageUrl : undefined,
  };
}

function extractOssKeyFromUrl(rawUrl?: string | null): string {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    const key = parsed.pathname.replace(/^\/+/, '').trim();
    if (key.startsWith('assets/') || key.startsWith('ttq/')) return key;
  } catch {
    // ignore
  }
  return '';
}
